import { iCategoryType } from "@/interfaces/categories";

(function (angular) {
    'use strict';

    angular.module('app')
    .controller('adminCategoriesController', adminCategoriesController);

    adminCategoriesController.$inject = [
        '$uibModal',
        'CategoriesApi',
        'CategoryTypesApi',
        'TreeManager',
        '_',
        'confirmDialog',
    ];

    /**
     * Controller for the manage categories admin page
     *
     * @returns {void}
     */
    function adminCategoriesController(
        $uibModal,
        CategoriesApi,
        CategoryTypesApi,
        TreeManager,
        _,
        confirmDialog,
    ) {
        const vm = this;

        // properties
        vm.isLoadingCategoryTypes = false;
        vm.isLoadingCategories = false;
        vm.categoryTypes = [];
        vm.categories = []; // only holds categories that match the selected categoryType
        vm.categoriesOriginal = [];
        vm.categoriesTree = null; // vm.categories represented as a tree
        vm.selectedCategoryType = null;
        vm.categoryBeingEdited = {}; // { {Number}: id, {String}: name }
        vm.treePluginOptions = {
            dropped: handleTreeNodeDrop,
        };

        // methods
        vm.editCategoryType = editCategoryType;
        vm.getCategoryTypes = getCategoryTypes;
        vm.rebuildTree = rebuildTree;
        vm.getCategories = getCategories;
        vm.setSelectedCategory = setSelectedCategory;
        vm.addNewCategory = addNewCategory;
        vm.saveNewCategory = saveNewCategory;
        vm.setCategoryBeingEdited = setCategoryBeingEdited;
        vm.cancelCategoryBeingEdited = cancelCategoryBeingEdited;
        vm.saveCategoryBeingEdited = saveCategoryBeingEdited;
        vm.saveCategories = saveCategories;
        vm.deleteCategory = deleteCategory;
        vm.deleteCategoryType = deleteCategoryType;

        /**
         * Opens a modal allowing a category type to be edited.
         *
         * @param {MouseEvent} event
         * @param {number=} categoryTypeId passing a null value will edit a new category type.
         * @returns {void}
         */
        function editCategoryType(event: MouseEvent, categoryTypeId?: number): void {
            event.preventDefault();

            const modal = $uibModal.open({
                controller: 'editCategoryTypeController',
                controllerAs: 'vm',
                resolve: {
                    categoryTypeId: () => categoryTypeId,
                },
                templateUrl: 'modals/edit-category-type.html',
            });

            // Reload category types if something was added or changed.
            modal.result.then((modifiedCategoryType?: iCategoryType): void => {
                if (!modifiedCategoryType) { return; }
                getCategoryTypes();
            });
        }

        /**
         * Retrieves category types from the API and assigns to vm.categoryTypes
         *
         * @returns {Void}
         */
        function getCategoryTypes() {
            vm.isLoadingCategoryTypes = true;
            CategoryTypesApi.query(
                { "categorizables[]": ['*']},
                function handleGetCategoryTypesSuccess(response) {
                    vm.isLoadingCategoryTypes = false;
                    vm.categoryTypes = response;
                },
                function handleGetCategoryTypesError() {
                    vm.isLoadingCategoryTypes = false;
                }
            );
        }

        /**
         * Builds vm.categoriesTree from vm.categories
         *
         * @returns {Void}
         */
        function rebuildTree() {
            vm.categoriesTree = TreeManager.arrayToTree(angular.copy(vm.categories));
        }

        /**
         * Updates vm.categories when the user moves a tree node to a new position
         *
         * @param {Object} event
         */
        function handleTreeNodeDrop() {
            // timeout of 0 ensures that this executes after vm.categoriesTree has been updated by the plugin
            setTimeout(function () {
                // save updated categories ready for saving to the API
                vm.categories = TreeManager.treeToArray(angular.copy(vm.categoriesTree));
                saveCategories();
            }, 0);
        }

        /**
         * Starts editing of a new category
         *
         * @param {Number} parentId
         */
        function addNewCategory(parentId) {
            vm.categoryBeingEdited = {
                id: null,
                parent_id: parentId,
                type_id: vm.selectedCategoryType.id,
                name: '',
            };
        }

        /**
         * Saves the category in vm.categoryBeingEdited to the API if it is new
         *
         * @returns {Void}
         */
        function saveNewCategory() {
            if (
                (vm.categoryBeingEdited.id !== null) ||
                (vm.categoryBeingEdited.name === '')
            ) {
                return false;
            }

            vm.isLoadingCategories = true;

            // do the save...
            CategoriesApi.post(vm.categoryBeingEdited,
                function handleSaveNewCategorySuccess(response) {
                    var category = angular.copy(response.data);
                    vm.categoryBeingEdited = {};
                    vm.categoriesOriginal.push(category);
                    vm.categories.push(category);
                    rebuildTree();
                    vm.isLoadingCategories = false;
                },
                function handleSaveNewCategoryError() {
                    vm.isLoadingCategories = false;
                }
            );
        }

        /**
         * Set the selected category
         *
         * @param {Object} categoryType
         * @returns {Void}
         */
        function setSelectedCategory(categoryType) {
            vm.selectedCategoryType = categoryType;
            getCategories(categoryType.id);
        }

        /**
         * Sets vm.categoryBeingEdited to a specified category, which will show an input
         * allowing the name to be changed.
         *
         * @param {Object} category
         * @returns {Void}
         */
        function setCategoryBeingEdited(category) {
            vm.categoryBeingEdited = angular.copy(category);
        }

        /**
         * Discards any changes to the category being edited
         *
         * @returns {Void}
         */
        function cancelCategoryBeingEdited() {
            vm.categoryBeingEdited = {};
        }

        /**
         * Updates the tree with the newly edited category
         */
        function saveCategoryBeingEdited() {
            if (vm.categoryBeingEdited.name === '') {
                return false;
            }

            var itemIndex = _.findIndex(vm.categories, { id: vm.categoryBeingEdited.id });
            vm.categories[itemIndex] = angular.copy(vm.categoryBeingEdited);
            rebuildTree();
            vm.categoryBeingEdited = {};

            setTimeout(saveCategories, 0);
        }

        /**
         * Deletes a category from the API and local data
         *
         * @param {Object} category
         */
        function deleteCategory(category) {
            function performDelete() {
                vm.isLoadingCategories = true;
                CategoriesApi.delete(
                    {id: category.id },
                    function handleDeleteCategorySuccess() {
                        cancelCategoryBeingEdited();
                        // reload categories as any children will now be deleted
                        getCategories(vm.selectedCategoryType.id);
                    },
                    function handleDeleteCategoryError() {
                        vm.isLoadingCategories = false;
                    }
                );
            }

            confirmDialog('Are you sure you want to delete this category?').result
                .then(function (canDelete) {
                    if (canDelete) {
                        performDelete();
                    }
                });
        }

        /**
         * Asks for confirmation before deleting a category type from the API
         *
         * @param {Object} categoryType
         * @returns {Void}
         */
        function deleteCategoryType(categoryType) {
            function performDelete() {
                vm.isLoadingCategoryTypes = true;
                CategoryTypesApi.delete(
                    { id: categoryType.id },
                    function handleDeleteCategoryTypeSuccess() {
                        var categoryTypeIndex = vm.categoryTypes.indexOf(categoryType);
                        // clear data if this category type was selected
                        if (vm.selectedCategoryType.id === categoryType.id) {
                            vm.selectedCategoryType = null;
                            vm.categories = [];
                            vm.categoriesTree = null;
                        }
                        vm.categoryTypes.splice([categoryTypeIndex], 1);
                        vm.isLoadingCategoryTypes = false;
                    },
                    function handleDeleteCategoryTypeError() {
                        vm.isLoadingCategoryTypes = false;
                    }
                );
            }

            confirmDialog('Are you sure you want to delete this group?').result
                .then(function (canDelete) {
                    if (canDelete) {
                        performDelete();
                    }
                });
        }

        /**
         * Retrieves categories from the API for a specified categoryType and assigns to vm.categories
         *
         * @param {Number} categoryTypeId
         * @returns {Void}
         */
        function getCategories(categoryTypeId) {
            vm.isLoadingCategories = true;
            CategoriesApi.get(
                {
                    type_id: categoryTypeId,
                },
                function handleGetCategoriesSuccess(response) {
                    var categories = angular.copy(response.data);
                    vm.isLoadingCategories = false;
                    vm.categories = categories;
                    vm.categoriesOriginal = angular.copy(categories); // save copy so we can compare any changes
                    rebuildTree();
                },
                function handleGetCategoriesError() {
                    vm.isLoadingCategories = false;
                }
            );
        }

        /**
         * Saves any modified categories to the API
         *
         * @param {Function} onCompleteCallback - returns the number of changed categories
         * @returns {Void}
         */
        function saveCategories(onCompleteCallback?: (numChangedCategories: number) => void) {
            onCompleteCallback = onCompleteCallback || angular.noop;

            vm.isLoadingCategories = true;
            var modifiedCategories = []; // { {Number}: originalCategoryIndex, {Number}: categoryIndex, {Object}: category, {Object}: originalCategory }


            vm.categories.forEach(function (category, categoryIndex) {

                var originalCategory = _.find(vm.categoriesOriginal, { id: category.id });
                var originalCategoryIndex = vm.categoriesOriginal.indexOf(originalCategory);

                category.position = categoryIndex;

                modifiedCategories.push({
                  category: category,
                  categoryIndex: categoryIndex,
                  originalCategory: originalCategory,
                  originalCategoryIndex: originalCategoryIndex,
                });
            });

            if (!modifiedCategories.length) {
                onCompleteCallback(0);
                return vm.isLoadingCategories = false;
            }

            // do stuff once all saves have completed
            var onComplete = _.after(modifiedCategories.length, function () {
                rebuildTree();
                onCompleteCallback(modifiedCategories.length);
                vm.isLoadingCategories = false;
            });

            // save each category
            modifiedCategories.forEach(function (item) {
                CategoriesApi.put(
                    item.category,
                    function handleSaveCategorySuccess() {
                        // update original category so next save calculates changes correctly
                        vm.categoriesOriginal[item.originalCategoryIndex] = angular.copy(item.category);
                        onComplete();
                    },
                    function handleSaveCategoryError() {
                        // undo the change
                        vm.categories[item.categoryIndex] = item.originalCategory;
                        onComplete();
                    }
                );
            });
        }

        (function main(){
            getCategoryTypes();
        })();
    }
})(angular);
